import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CHECKLIST_RUN_STATUSES, PERMISSIONS } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Card,
  EmptyState,
  ListRow,
  ProgressBar,
  Select,
  SkeletonRows,
  StatusPill,
  runStatusTone,
} from '../../components/ui';
import { api, qs } from '../../lib/api';
import { useCan } from '../../lib/auth';
import { formatDateTime, humanise } from '../../lib/format';

interface RunRow {
  id: string;
  status: string;
  shift: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  totalItems: number;
  completedItems: number;
  abnormalCount: number;
  problemCount: number;
  templateName: string;
  machineCode: string;
  machineName: string;
  lineName: string;
  operatorName: string;
}

export function RunListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canViewAll = useCan(PERMISSIONS.RUN_VIEW_ALL);

  const status = params.get('status') ?? '';
  const machineId = params.get('machineId') ?? '';
  const mine = params.get('mine') ?? (canViewAll ? '' : '1');

  const { data, isLoading } = useQuery({
    queryKey: ['runs', { status, machineId, mine }],
    queryFn: () =>
      api.get<{ data: RunRow[]; total: number }>(
        `/api/checklist-runs${qs({ status, machineId, mine, pageSize: 50 })}`,
      ),
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Checklists"
        subtitle={canViewAll ? 'CILT runs across the pilot lines' : 'Your CILT checks'}
      />

      <div className="space-y-4 p-4 lg:p-6">
        <Card className="p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <Select
              value={status}
              onChange={(event) => setParam('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {CHECKLIST_RUN_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </Select>
            {canViewAll ? (
              <Select
                value={mine}
                onChange={(event) => setParam('mine', event.target.value)}
                aria-label="Filter by operator"
              >
                <option value="">Everyone</option>
                <option value="1">Only mine</option>
              </Select>
            ) : null}
          </div>
        </Card>

        <Card>
          {isLoading ? (
            <SkeletonRows rows={6} />
          ) : !data?.data.length ? (
            <EmptyState
              icon={ClipboardList}
              title="No checklists here"
              message="Nothing matches these filters. Scheduled CILT checks appear on your home screen when they are due."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.data.map((run) => (
                <li key={run.id}>
                  <ListRow
                    onClick={() =>
                      navigate(
                        run.status === 'IN_PROGRESS' || run.status === 'PARTIALLY_COMPLETED'
                          ? `/checklists/${run.id}`
                          : `/checklists/${run.id}/review`,
                      )
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-steel-900">
                          {run.machineCode} · {run.templateName}
                        </p>
                        <p className="truncate text-xs text-steel-500">
                          {run.operatorName}
                          {run.shift ? ` · Shift ${run.shift}` : ''} · {run.lineName} ·{' '}
                          {formatDateTime(run.startedAt)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <StatusPill tone={runStatusTone(run.status)} label={humanise(run.status)} />
                        {run.abnormalCount > 0 ? (
                          <span className="text-xs font-bold text-warn-700">
                            {run.abnormalCount} abnormal
                          </span>
                        ) : null}
                        {run.problemCount > 0 ? (
                          <span className="text-xs font-bold text-alarm-700">
                            {run.problemCount} tag{run.problemCount === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {run.status === 'IN_PROGRESS' ? (
                      <div className="mt-2">
                        <ProgressBar
                          value={run.completedItems}
                          total={run.totalItems}
                          tone="info"
                          label="Tasks"
                        />
                      </div>
                    ) : null}
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
